// Componente de presentación que encapsula los campos de datos de envío del pedido.
import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Label } from '@shared/components/ui/label';
import { Input } from '@shared/components/ui/input';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { useTranslation } from 'react-i18next';
import { useCountries } from '../../hooks';

interface ShippingDataProps {
  isLocked?: boolean;
}

export interface ShippingDataRef {
  getFormData: () => {
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    address1: string;
    address2: string;
    postalCode: string;
    city: string;
    province: string;
    country: string;
    countryId: number;
  };
  setFormData: (data: {
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    address1: string;
    address2: string;
    postalCode: string;
    city: string;
    province: string;
    country: string;
    countryId?: string;
  }) => void;
}

export const ShippingData = forwardRef<ShippingDataRef, ShippingDataProps>(({ isLocked = false }, ref) => {
  const { t } = useTranslation('createEditOrdersModule');
  const { countries, loading: countriesLoading } = useCountries();
  const isDisabled = isLocked;

  const [formData, setFormData] = useState({
    // Cliente
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    // Dirección
    address1: '',
    address2: '',
    postalCode: '',
    city: '',
    province: '',
    country: '',
    countryId: 0,
  });

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCountryChange = (value: string) => {
    handleInputChange('country', value);
    // Encontrar el país seleccionado para obtener su ID
    const selectedCountry = countries.find((country) => country.iso2 === value);
    if (selectedCountry) {
      handleInputChange('countryId', selectedCountry.id.toString());
    }
  };

  // Actualizar countryId cuando se carguen los países y ya haya un country establecido
  useEffect(() => {
    if (formData.country && countries.length > 0 && formData.countryId === 0) {
      const selectedCountry = countries.find((country) => country.iso2 === formData.country);
      if (selectedCountry) {
        handleInputChange('countryId', selectedCountry.id);
      }
    }
  }, [countries, formData.country, formData.countryId]);

  // Convertir los datos de la API a opciones para el combobox de países
  const countryOptions = countries.map((country) => ({
    label: country.name,
    value: country.iso2,
  }));

  // Exponer los datos del formulario al componente padre
  useImperativeHandle(ref, () => ({
    getFormData: () => formData,
    setFormData: (data) => {
      // Si se proporciona un country, buscar el countryId correspondiente
      let countryId = parseInt(data.countryId || '0', 10);
      if (data.country && countries.length > 0) {
        const selectedCountry = countries.find((country) => country.iso2 === data.country);
        if (selectedCountry) {
          countryId = selectedCountry.id;
        }
      }

      setFormData({
        ...data,
        countryId,
      });
    },
  }));

  return (
    <div className="p-4 space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-5 pt-4 border-b border-card-border">
      <div className="col-span-1 md:col-span-2">
        <h2 className="text-lg font-bold">{t('shippingData.title')}</h2>
      </div>
      <div className="col-span-1 md:col-span-3 !mt-0">
        <div className="space-y-6">
          {/* Datos del cliente */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold">{t('shippingData.customer.title')}</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-2 lg:col-span-2">
                <Label htmlFor="customerName">{t('shippingData.customer.fields.name.label')}</Label>
                <Input
                  id="customerName"
                  type="text"
                  placeholder={t('shippingData.customer.fields.name.placeholder')}
                  value={formData.customerName}
                  onChange={(e) => handleInputChange('customerName', e.target.value)}
                  maxLength={250}
                  disabled={isDisabled}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerPhone">{t('shippingData.customer.fields.phone.label')}</Label>
                <Input
                  id="customerPhone"
                  type="tel"
                  placeholder={t('shippingData.customer.fields.phone.placeholder')}
                  value={formData.customerPhone}
                  onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                  maxLength={250}
                  disabled={isDisabled}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerEmail">{t('shippingData.customer.fields.email.label')}</Label>
                <Input
                  id="customerEmail"
                  type="email"
                  placeholder={t('shippingData.customer.fields.email.placeholder')}
                  value={formData.customerEmail}
                  onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                  maxLength={250}
                  disabled={isDisabled}
                />
              </div>
            </div>
          </div>

          {/* Dirección */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold">{t('shippingData.address.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="address1">{t('shippingData.address.fields.address1.label')}</Label>
                <Input
                  id="address1"
                  type="text"
                  placeholder={t('shippingData.address.fields.address1.placeholder')}
                  value={formData.address1}
                  onChange={(e) => handleInputChange('address1', e.target.value)}
                  maxLength={250}
                  disabled={isDisabled}
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="address2">{t('shippingData.address.fields.address2.label')}</Label>
                <Input
                  id="address2"
                  type="text"
                  placeholder={t('shippingData.address.fields.address2.placeholder')}
                  value={formData.address2}
                  onChange={(e) => handleInputChange('address2', e.target.value)}
                  maxLength={250}
                  disabled={isDisabled}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postalCode">{t('shippingData.address.fields.postalCode.label')}</Label>
                <Input
                  id="postalCode"
                  type="text"
                  placeholder={t('shippingData.address.fields.postalCode.placeholder')}
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  maxLength={250}
                  disabled={isDisabled}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">{t('shippingData.address.fields.city.label')}</Label>
                <Input
                  id="city"
                  type="text"
                  placeholder={t('shippingData.address.fields.city.placeholder')}
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  maxLength={250}
                  disabled={isDisabled}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="province">{t('shippingData.address.fields.province.label')}</Label>
                <Input
                  id="province"
                  type="text"
                  placeholder={t('shippingData.address.fields.province.placeholder')}
                  value={formData.province}
                  onChange={(e) => handleInputChange('province', e.target.value)}
                  maxLength={250}
                  disabled={isDisabled}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">{t('shippingData.address.fields.country.label')}</Label>
                <Combobox
                  options={countryOptions}
                  placeholder={t('shippingData.address.fields.country.placeholder')}
                  value={formData.country}
                  setValue={handleCountryChange}
                  className="w-full"
                  disabled={countriesLoading || isDisabled}
                  dataTestId="shipping-country-combobox"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
